import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  MapPin, 
  Calendar, 
  User, 
  Phone,
  FileText,
  AlertCircle,
  CheckCircle,
  Clock,
  Download,
  MessageSquare,
  RefreshCw
} from 'lucide-react';
import { API_BASE_URL } from '../services/api';
import toast from 'react-hot-toast';

const CitizenReportDetail: React.FC = () => {
  const { reportId } = useParams<{ reportId: string }>();
  const navigate = useNavigate();
  const [report, setReport] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updates, setUpdates] = useState<any[]>([]);

  useEffect(() => {
    if (reportId) {
      fetchReportDetails();
    } else {
      setError('Report ID is missing');
      setLoading(false);
    }
  }, [reportId]);

  const fetchReportDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Session expired. Please login again.');
        setLoading(false);
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/v1/citizen-reports/${reportId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Report not found');
        }
        if (response.status === 401) {
          throw new Error('Session expired. Please login again.');
        }
        throw new Error(`Failed to load report: ${response.statusText}`);
      }

      const data = await response.json();
      
      if (data.success && data.data) {
        setReport(data.data);
        fetchReportUpdates(data.data.reportId || reportId);
      } else {
        throw new Error(data.error || 'Invalid response format');
      }
    } catch (error: any) {
      console.error('Error fetching report:', error);
      setError(error.message || 'Failed to load report details');
      toast.error(error.message || 'Failed to load report details');
    } finally {
      setLoading(false);
    }
  };

  const fetchReportUpdates = async (id: string) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      try {
        const response = await fetch(`${API_BASE_URL}/api/v1/citizen-reports/${id}/updates`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data) {
            setUpdates(data.data);
            return;
          }
        }
      } catch (error) {
        console.log('Updates endpoint not available, using mock data');
      }

      const mockUpdates = [
        {
          id: 1,
          text: 'Report has been received and is being reviewed.',
          date: report?.reportedAt || new Date().toISOString(),
          user: 'System'
        }
      ];

      if (report?.status?.toUpperCase() === 'IN_PROGRESS') {
        mockUpdates.push({
          id: 2,
          text: 'Report has been assigned to the appropriate department for action.',
          date: new Date(Date.now() - 86400000).toISOString(),
          user: 'Admin'
        });
      }

      if (report?.status?.toUpperCase() === 'RESOLVED') {
        mockUpdates.push({
          id: 2,
          text: 'Report has been assigned to the appropriate department for action.',
          date: new Date(Date.now() - 86400000).toISOString(),
          user: 'Admin'
        });
        mockUpdates.push({
          id: 3,
          text: 'Issue has been successfully resolved.',
          date: new Date(Date.now() - 3600000).toISOString(),
          user: 'Department Officer'
        });
      }

      setUpdates(mockUpdates);
    } catch (error) {
      console.warn('Could not fetch updates:', error);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'RESOLVED': return <CheckCircle className="text-[#3c8d2e]" size={20} />;
      case 'IN_PROGRESS': return <Clock className="text-[#0b2045]" size={20} />;
      case 'PENDING': return <Clock className="text-[#ca8a04]" size={20} />;
      case 'URGENT': return <AlertCircle className="text-red-500" size={20} />;
      default: return <AlertCircle className="text-gray-500" size={20} />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'RESOLVED': return 'bg-[#3c8d2e]/10 text-[#3c8d2e] border-[#3c8d2e]/30';
      case 'IN_PROGRESS': return 'bg-[#0b2045]/10 text-[#0b2045] border-[#0b2045]/30';
      case 'PENDING': return 'bg-[#fefce8] text-[#ca8a04] border-[#ca8a04]/30';
      case 'URGENT': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const handleRetry = () => {
    fetchReportDetails();
  };

  const handleDownload = () => {
    toast.success('Download feature coming soon!');
  };

  const handleAddComment = () => {
    toast.success('Comment feature coming soon!');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#f4f7f6]">
        <div className="text-center">
          <RefreshCw className="animate-spin mx-auto mb-4 text-[#0b2045]" size={40} />
          <p className="text-gray-600">Loading report details...</p>
        </div>
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className="min-h-screen bg-[#f4f7f6] p-4 md:p-6">
        <div className="max-w-4xl mx-auto">
          <button
            onClick={() => navigate('/citizen/reports')}
            className="flex items-center text-[#3c8d2e] hover:text-[#2d7022] mb-6 transition-colors"
          >
            <ArrowLeft size={18} className="mr-2" />
            Back to Reports
          </button>
          <div className="bg-white rounded-xl shadow-sm p-8 text-center border border-[#0b2045]/10">
            <AlertCircle className="mx-auto text-red-500 mb-4" size={48} />
            <h3 className="text-lg font-medium text-[#0b2045] mb-2">
              {error || 'Report not found'}
            </h3>
            <p className="text-gray-600 mb-4">
              {error?.includes('Session expired') 
                ? 'Please login again to continue.' 
                : 'The report you\'re looking for may have been removed or doesn\'t exist.'}
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <button
                onClick={handleRetry}
                className="px-4 py-2 bg-[#3c8d2e] text-white rounded-lg hover:bg-[#2d7022] transition-colors"
              >
                Try Again
              </button>
              <button
                onClick={() => navigate('/citizen/reports')}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-[#f4f7f6] transition-colors"
              >
                View All Reports
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f4f7f6] p-4 md:p-6">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <button
            onClick={() => navigate('/citizen/reports')}
            className="flex items-center text-gray-600 hover:text-[#0b2045] mb-4 transition-colors"
          >
            <ArrowLeft size={20} className="mr-2" />
            Back to Reports
          </button>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white rounded-xl shadow-sm p-4 md:p-6 border border-[#0b2045]/10">
            <div>
              <h1 className="text-2xl font-bold text-[#0b2045]">Report Details</h1>
              <p className="text-gray-500 text-sm">ID: {report.reportId || report.id}</p>
            </div>
            <div className="flex items-center gap-3">
              <span className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium border ${getStatusColor(report.status)}`}>
                {getStatusIcon(report.status)}
                <span className="ml-2">{report.status?.replace('_', ' ') || 'Unknown'}</span>
              </span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-xl shadow-sm p-6 border border-[#0b2045]/10">
              <h2 className="text-lg font-semibold text-[#0b2045] mb-4">Issue Details</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <p className="text-gray-900 bg-[#f4f7f6] p-3 rounded-lg">{report.description}</p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                    <p className="text-gray-900">{report.categoryDisplayName || report.category}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Urgency Score</label>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div 
                          className={`h-full rounded-full ${
                            (report.urgencyScore || 0) >= 7 ? 'bg-red-500' :
                            (report.urgencyScore || 0) >= 5 ? 'bg-[#ca8a04]' :
                            'bg-[#3c8d2e]'
                          }`}
                          style={{ width: `${(report.urgencyScore || 0) * 10}%` }}
                        ></div>
                      </div>
                      <span className="font-medium text-sm">{report.urgencyScore || 0}/10</span>
                    </div>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                  <div className="flex items-center text-gray-900">
                    <MapPin size={16} className="mr-2 text-gray-400" />
                    {report.wardName || report.constituencyName || 'Not specified'}
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Reported Date</label>
                    <div className="flex items-center text-gray-900">
                      <Calendar size={16} className="mr-2 text-gray-400" />
                      {report.reportedAt ? new Date(report.reportedAt).toLocaleString() : 'N/A'}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Last Updated</label>
                    <div className="flex items-center text-gray-900">
                      <Calendar size={16} className="mr-2 text-gray-400" />
                      {report.updatedAt ? new Date(report.updatedAt).toLocaleString() : 'N/A'}
                    </div>
                  </div>
                </div>

                {report.resolutionDetails && (
                  <div className="mt-4 p-4 bg-[#3c8d2e]/10 rounded-lg border border-[#3c8d2e]/30">
                    <label className="block text-sm font-medium text-[#3c8d2e] mb-1">Resolution Details</label>
                    <p className="text-sm text-[#3c8d2e]/90">{report.resolutionDetails}</p>
                  </div>
                )}
              </div>
            </div>

            {updates.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm p-6 border border-[#0b2045]/10">
                <h2 className="text-lg font-semibold text-[#0b2045] mb-4">Updates</h2>
                <div className="space-y-4">
                  {updates.map((update, index) => (
                    <div key={update.id} className="flex gap-4">
                      <div className="flex flex-col items-center">
                        <div className={`w-3 h-3 rounded-full ${
                          index === 0 ? 'bg-[#0b2045]' : 'bg-gray-300'
                        }`}></div>
                        {index < updates.length - 1 && (
                          <div className="w-0.5 h-full bg-gray-200 mt-1"></div>
                        )}
                      </div>
                      <div className="flex-1 pb-4">
                        <p className="text-[#0b2045]">{update.text}</p>
                        <div className="flex items-center gap-2 mt-2 text-sm text-gray-500">
                          <User size={14} />
                          <span>{update.user}</span>
                          <Calendar size={14} />
                          <span>{new Date(update.date).toLocaleString()}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm p-6 border border-[#0b2045]/10">
              <h2 className="text-lg font-semibold text-[#0b2045] mb-4">Reporter Information</h2>
              <div className="space-y-3">
                <div className="flex items-start">
                  <User size={16} className="text-gray-400 mr-3 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-600">Name</p>
                    <p className="font-medium text-[#0b2045]">{report.fullName || 'Anonymous'}</p>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <Phone size={16} className="text-gray-400 mr-3 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-600">Phone Number</p>
                    <p className="font-medium text-[#0b2045]">{report.phoneNumber || 'Not provided'}</p>
                  </div>
                </div>

                {report.email && (
                  <div className="flex items-start">
                    <User size={16} className="text-gray-400 mr-3 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-600">Email</p>
                      <p className="font-medium text-[#0b2045]">{report.email}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm p-6 border border-[#0b2045]/10">
              <h2 className="text-lg font-semibold text-[#0b2045] mb-4">Actions</h2>
              <div className="space-y-3">
                <button 
                  onClick={handleAddComment}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-[#f4f7f6] transition-colors"
                >
                  <MessageSquare size={16} />
                  Add Comment
                </button>
                <button 
                  onClick={handleDownload}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 border border-[#0b2045] text-[#0b2045] rounded-lg hover:bg-[#f4f7f6] transition-colors"
                >
                  <Download size={16} />
                  Download Report
                </button>
                {report.status !== 'RESOLVED' && (
                  <button className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-[#0b2045] text-white rounded-lg hover:bg-[#152c53] transition-colors">
                    <AlertCircle size={16} />
                    Mark as Urgent
                  </button>
                )}
              </div>
            </div>

            {(report.wardName || report.constituencyName) && (
              <div className="bg-white rounded-xl shadow-sm p-6 border border-[#0b2045]/10">
                <h2 className="text-lg font-semibold text-[#0b2045] mb-4">Location Details</h2>
                <div className="space-y-3">
                  {report.wardName && (
                    <div>
                      <p className="text-sm text-gray-600">Ward</p>
                      <p className="font-medium text-[#0b2045]">{report.wardName}</p>
                    </div>
                  )}
                  {report.constituencyName && (
                    <div>
                      <p className="text-sm text-gray-600">Constituency</p>
                      <p className="font-medium text-[#0b2045]">{report.constituencyName}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CitizenReportDetail;